import { jsx as _jsx } from "preact/jsx-runtime";
/* eslint-disable @typescript-eslint/no-unused-vars */
import { createContext, useCallback, useContext, useMemo, useState, } from 'preact/compat';
import convertSessionToDate from '@widgets/shared/convertSessionToDate';
import { addMinutes, subMinutes } from 'date-fns/fp';
const TimeConflictContext = createContext({
    convertActivityToSession: (activity) => null,
    addSelectedTimePeriod: (session) => null,
    overwriteTimePeriods: (session) => [],
    initTimePeriods: (sessions) => null,
    checkTimeConflicts: (session, activity) => false,
    selectedTimePeriods: [],
});
// This hook can be used to access the user info.
export function useTimeConflict() {
    const value = useContext(TimeConflictContext);
    //
    // if (!value) {
    //   throw new Error('useFaqs must be wrapped in a <TimeConflictContext />');
    // }
    return value ?? {};
}
// eslint-disable-next-line max-len
export function hasTimeConflicts(selectedTimePeriods, startDateToCheck, endDateToCheck, activityId) {
    const conflictChecks = selectedTimePeriods
        .filter((timePeriod) => timePeriod.activity.activity.id !== activityId)
        .reduce((carry, value) => {
        const startIsBetween = startDateToCheck >= value.from && startDateToCheck <= value.to;
        const endIsBetween = endDateToCheck >= value.from && endDateToCheck <= value.to;
        const activitiesOverlap = endDateToCheck >= value.to && startDateToCheck <= value.from;
        return [...carry, startIsBetween || endIsBetween || activitiesOverlap];
    }, [])
        .filter(Boolean);
    return !!conflictChecks.length;
}
export function TimeConflictProvider(props) {
    const { children } = props;
    const [selectedTimePeriods, setSelectedTimePeriod] = useState([]);
    /**
     * use this if you have a list of activities with sessions you need to set at once, i.e. if setting from query params.
     */
    const initTimePeriods = useCallback((sessions) => {
        console.log('Setting time periods to ', sessions);
        setSelectedTimePeriod(sessions);
    }, []);
    const addSelectedTimePeriod = useCallback((value) => setSelectedTimePeriod([
        ...selectedTimePeriods.filter((d) => {
            const { activity: thisActivity } = d;
            const { activity: selectedActivity } = value;
            return thisActivity.activity.id !== selectedActivity.activity.id;
        }),
        value,
    ]), [selectedTimePeriods, setSelectedTimePeriod]);
    // const overwriteTimePeriods = useCallback((activityToKeep: number) => selectedTimePeriods.filter((period) => period.activity.activity.id === activityToKeep), [selectedTimePeriods, setSelectedTimePeriod]);
    const checkTimeConflicts = useCallback((session, activity) => {
        const sessionDate = convertSessionToDate(session);
        const { overlapBefore, overlapAfter, durationInMinutes, id: activityId, } = activity;
        return hasTimeConflicts(selectedTimePeriods, subMinutes((overlapBefore ?? 0), sessionDate), addMinutes(durationInMinutes + (overlapAfter ?? 0), sessionDate), activityId);
    }, [selectedTimePeriods]);
    // if we click on a disabled session we need to remove current overlapping options and force add this.
    const overwriteTimePeriods = useCallback((value) => {
        const selectedTimePeriodsCopy = [...selectedTimePeriods];
        let newSelectedTimePeriods = [];
        // ensuring the selected time period is in the array as may have been removed at this point...
        if (selectedTimePeriodsCopy.find((timePeriod) => timePeriod.activity.activity.id === value.activity.activity.id) === undefined) {
            selectedTimePeriodsCopy.push({
                from: value.from,
                to: value.to,
                activity: value.activity,
            });
            newSelectedTimePeriods = selectedTimePeriodsCopy;
        }
        else {
            newSelectedTimePeriods = selectedTimePeriods
                .map((timePeriod) => {
                if (timePeriod.activity.activity.id === value.activity.activity.id) {
                    return {
                        from: value.from,
                        to: value.to,
                        activity: value.activity,
                    };
                }
                return timePeriod;
            });
        }
        console.log('Timeconflicts.tsx - Old Time Periods - ', selectedTimePeriods);
        console.log('Timeconflicts.tsx - new Time Periods - ', newSelectedTimePeriods);
        let hasConflicts = false;
        newSelectedTimePeriods = newSelectedTimePeriods.filter((timePeriod) => {
            if (timePeriod.activity.activity.id === value.activity.activity.id) {
                return true;
            }
            const activityHasConflicts = hasTimeConflicts(newSelectedTimePeriods, timePeriod.from, timePeriod.to, timePeriod.activity.activity.id);
            if (activityHasConflicts) {
                hasConflicts = true;
            }
            return !activityHasConflicts;
        });
        if (hasConflicts) {
            window.dispatchEvent(new CustomEvent('dayticket.overlaps'));
        }
        setSelectedTimePeriod(newSelectedTimePeriods);
        return newSelectedTimePeriods;
    }, [selectedTimePeriods, setSelectedTimePeriod]);
    const convertActivityToSession = useCallback((activity) => {
        if (!activity.activeSession) {
            return null;
        }
        const convertedSessionDate = convertSessionToDate(activity.activeSession);
        const { durationInMinutes, overlapBefore, overlapAfter } = activity.activity;
        return {
            activity,
            from: subMinutes(overlapBefore ?? 0, convertedSessionDate),
            to: addMinutes(durationInMinutes + (overlapAfter ?? 0), convertedSessionDate),
        };
    }, []);
    const value = useMemo(() => ({
        addSelectedTimePeriod,
        selectedTimePeriods,
        checkTimeConflicts,
        initTimePeriods,
        overwriteTimePeriods,
        convertActivityToSession,
    }), [
        addSelectedTimePeriod,
        selectedTimePeriods,
        checkTimeConflicts,
        initTimePeriods,
        overwriteTimePeriods,
        convertActivityToSession,
    ]);
    return (_jsx(TimeConflictContext.Provider, { value: value, children: children }));
}
